import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createContext } from "react";
import { useTranslation } from "react-i18next";
import { useCompany } from "@/api";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";
import PageBackButton from "@/components/PageBackButton";
import { Breadcrumbs, Button } from "@app-components";
import { useLoggedInUser } from "@/store/zustandStore";
import { useSwitchCompany } from "@/components/app/useSwitchCompany";
import CompanySettingsForm from "@/components/company_admin/company_settings/CompanySettingsForm";

export const Route = createFileRoute("/_portal/building/companies/$companyId")({
  component: CompanyDetails,
});

export const CompanyIdContext = createContext("");

function CompanyDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = Route.useParams();
  const { allowBuildingAdminOrReceptionistAccess } =
    useBuildingAdminOrReceptionistAccess();
  const { data: company } = useCompany(companyId);
  const user = useLoggedInUser();
  const { pendingCompanyId, prefetchCompany, switchCompany } =
    useSwitchCompany();

  const breadcrumbs = [
    {
      label: t("SIDEBAR.BUILDING_COMPANIES"),
      path: "/building/companies",
    },
    {
      label: company.buildingTenantName,
      path: `/building/companies/${companyId}`,
    },
  ];

  if (!allowBuildingAdminOrReceptionistAccess) {
    return null;
  }
  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/companies",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1 style={{ marginBottom: "var(--wlcm-spacing-xs)" }}>
          {company.buildingTenantName}
        </h1>
        {user.adminInCompanies?.includes(companyId) && (
          <Button
            icon="work"
            secondary
            isLoading={pendingCompanyId === companyId}
            disabled={!!pendingCompanyId}
            aria-busy={pendingCompanyId === companyId}
            onMouseEnter={() => prefetchCompany(companyId)}
            onFocus={() => prefetchCompany(companyId)}
            onClick={() => switchCompany(companyId)}
          >
            {t("CONTEXT.OPEN_COMPANY_WORKSPACE")}
          </Button>
        )}
      </div>

      <CompanySettingsForm companyId={companyId} buildingAdminPage={true} />
    </>
  );
}
